import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getWizardState, advanceWizardStep } from "../api/wizard.js";
import type {
  WizardState,
  WizardStepName,
  WizardOutcome,
} from "../api/types.js";

export function useWizardState(websiteId: string | undefined) {
  return useQuery<WizardState>({
    queryKey: ["wizard", websiteId],
    queryFn: () => {
      if (!websiteId) throw new Error("websiteId is required");
      return getWizardState(websiteId);
    },
    enabled: websiteId != null,
  });
}

export interface AdvanceStepArgs {
  step: WizardStepName;
  outcome: WizardOutcome;
  data?: Record<string, unknown>;
}

export function useAdvanceWizardStep(websiteId: string) {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; state: WizardState },
    Error,
    AdvanceStepArgs
  >({
    mutationFn: (args) => advanceWizardStep(websiteId, args),
    onSuccess: ({ state }) => {
      qc.setQueryData(["wizard", websiteId], state);
    },
  });
}

/**
 * Returns the data payload stored in a completed wizard step, if any.
 */
export function useStepData(
  state: WizardState | undefined,
  step: WizardStepName,
): Record<string, unknown> | null {
  if (!state) return null;
  return state.steps.find((s) => s.step === step)?.data ?? null;
}
